import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';

export interface SortableVariableListProps {
  /** Ordered, stable keys of the draggable rows only; pinned rows are left out. */
  itemIds: string[];
  /** Called once per completed drag with the dragged row's key and the key of
   * the row it was dropped on; not called for a drop on the same row. */
  onReorder: (activeId: string, overId: string) => void;
  children: ReactNode;
}

/** Drag-and-drop context for the analytical dataset's variable list. The
 * pointer needs to move a few pixels before a drag starts, so a click on a
 * row's link or delete button is not taken for a drag. */
export const SortableVariableList = ({
  itemIds,
  onReorder,
  children,
}: SortableVariableListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
